import React, { useEffect, useState } from 'react'
import { Check } from 'lucide-react'
import { Button } from '../../components/ui'
import { resolveGroupSize, resolveTeamsAdvancing } from '../tournamentStandings'
import { STATUS_LABELS, fromLocalInput, toLocalInput } from './shared'

const formFrom = (tournament) => ({
  name: tournament.name || '',
  description: tournament.description || '',
  locationName: tournament.locationName || '',
  maxPlayersPerTeam: tournament.maxPlayersPerTeam || 5,
  groupSize: resolveGroupSize(tournament),
  teamsAdvancingPerGroup: resolveTeamsAdvancing(tournament),
  status: tournament.status || 'OPEN',
  visibility: tournament.visibility || 'PUBLIC',
  startsAt: toLocalInput(tournament.startsAt),
  endsAt: toLocalInput(tournament.endsAt)
})

const DANGER_ACTIONS = [
  {
    key: 'regenerate',
    title: 'Trekk kampoppsett på nytt',
    help: 'Lager nye grupper og kamper fra lagene som er påmeldt.',
    question: 'Trekke kampoppsettet på nytt? Lagene trekkes tilfeldig, og dagens kampoppsett erstattes.',
    ok: 'Trekk på nytt'
  },
  {
    key: 'cancel',
    title: 'Avlys turnering',
    help: 'Statusen settes til Avlyst. Turneringen blir liggende, men kan ikke spilles.',
    question: 'Avlyse turneringen?',
    ok: 'Avlys'
  },
  {
    key: 'delete',
    title: 'Slett turnering',
    help: 'Fjerner turneringen, lag, kamper og produkter.',
    question: 'Slette turneringen permanent? Dette kan ikke angres.',
    ok: 'Slett'
  }
]

function SettingsTab({ tournament, canManage, saving, onSave, onRegenerate, onCancel, onDelete, justSaved }) {
  const isGroups = tournament.format === 'GROUPS_THEN_KNOCKOUT'
  const [form, setForm] = useState(() => formFrom(tournament))
  const [confirming, setConfirming] = useState(null)

  useEffect(() => {
    setForm(formFrom(tournament))
  }, [tournament])

  const save = (e) => {
    e.preventDefault()
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      locationName: form.locationName.trim(),
      maxPlayersPerTeam: Number(form.maxPlayersPerTeam),
      status: form.status,
      visibility: form.visibility,
      startsAt: fromLocalInput(form.startsAt),
      endsAt: fromLocalInput(form.endsAt)
    }
    if (isGroups) {
      const gs = Math.min(16, Math.max(2, Number(form.groupSize) || 4))
      payload.groupSize = gs
      payload.teamsAdvancingPerGroup = Math.min(gs, Math.max(1, Number(form.teamsAdvancingPerGroup) || 2))
    }
    onSave(payload)
  }

  const handlers = { regenerate: onRegenerate, cancel: onCancel, delete: onDelete }
  const confirmAction = (key) => {
    setConfirming(null)
    handlers[key]()
  }

  const disabled = !canManage || saving

  return (
    <div className="tn-settings-layout">
      <section className="card tn-card tn-settings-main" aria-labelledby="tn-eo-h">
        <div className="tn-head-row center">
          <h2 id="tn-eo-h" className="section-title">Endre oppsett</h2>
          {justSaved && (
            <span role="status" className="pill pill-open pill-lg">
              <Check size={14} strokeWidth={2.6} aria-hidden="true" />Lagret
            </span>
          )}
        </div>
        <form onSubmit={save} className="tn-settings-form">
          <label className="field span-all">
            <span className="field-label">Navn *</span>
            <input
              disabled={disabled}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label className="field span-all">
            <span className="field-label">Sted</span>
            <input
              disabled={disabled}
              value={form.locationName}
              onChange={(e) => setForm({ ...form, locationName: e.target.value })}
            />
          </label>
          <label className="field span-all">
            <span className="field-label">Beskrivelse</span>
            <textarea
              disabled={disabled}
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field-label">Status</span>
            <select
              disabled={disabled}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <fieldset className="field tn-fieldset">
            <legend className="field-label">Synlighet</legend>
            <div className="tn-radio-seg">
              {[['PUBLIC', 'Offentlig'], ['PRIVATE', 'Privat']].map(([value, label]) => (
                <label key={value} className={form.visibility === value ? 'on' : ''}>
                  <input
                    type="radio"
                    name="tn-visibility"
                    value={value}
                    checked={form.visibility === value}
                    disabled={disabled}
                    onChange={() => setForm({ ...form, visibility: value })}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="field">
            <span className="field-label">Spillere per lag</span>
            <input
              type="number"
              className="input-mono"
              min={1}
              max={30}
              disabled={disabled}
              value={form.maxPlayersPerTeam}
              onChange={(e) => setForm({ ...form, maxPlayersPerTeam: e.target.value })}
            />
            <span className="field-hint">1–30</span>
          </label>
          {isGroups && (
            <>
              <label className="field">
                <span className="field-label">Lag per gruppe</span>
                <input
                  type="number"
                  className="input-mono"
                  min={2}
                  max={16}
                  disabled={disabled}
                  value={form.groupSize}
                  onChange={(e) => {
                    const next = Number(e.target.value) || 4
                    setForm({
                      ...form,
                      groupSize: next,
                      teamsAdvancingPerGroup: Math.min(form.teamsAdvancingPerGroup, next)
                    })
                  }}
                />
                <span className="field-hint">2–16</span>
              </label>
              <label className="field">
                <span className="field-label">Videre per gruppe</span>
                <input
                  type="number"
                  className="input-mono"
                  min={1}
                  max={form.groupSize || 16}
                  disabled={disabled}
                  value={form.teamsAdvancingPerGroup}
                  onChange={(e) => setForm({ ...form, teamsAdvancingPerGroup: e.target.value })}
                />
                <span className="field-hint">Maks lag per gruppe</span>
              </label>
            </>
          )}
          <label className="field">
            <span className="field-label">Starter</span>
            <input
              type="datetime-local"
              disabled={disabled}
              value={form.startsAt}
              onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field-label">Slutter</span>
            <input
              type="datetime-local"
              disabled={disabled}
              value={form.endsAt}
              onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
            />
          </label>
          {isGroups && (
            <p className="tn-footnote span-all">
              Endring av gruppeoppsett regenererer kampoppsett hvis turneringen ikke har startet.
            </p>
          )}
          {canManage && (
            <div className="tn-form-foot span-all">
              <Button size="lg" type="submit" disabled={saving}>
                {saving ? 'Lagrer…' : 'Lagre endringer'}
              </Button>
            </div>
          )}
        </form>
      </section>

      {canManage && (
        <section className="danger-zone tn-danger" aria-labelledby="tn-fz-h">
          <h2 id="tn-fz-h" className="section-title tn-danger-title">Faresone</h2>
          {DANGER_ACTIONS.map((d) => (
            <div key={d.key} className="tn-danger-row">
              <div className="stack-sm">
                <span className="tn-danger-name">{d.title}</span>
                <span className="tn-footnote">{d.help}</span>
              </div>
              {confirming === d.key ? (
                <div className="inline-confirm tn-inline-confirm" role="alertdialog" aria-label="Bekreft" aria-describedby={`tn-q-${d.key}`}>
                  <span id={`tn-q-${d.key}`} className="tn-confirm-q">{d.question}</span>
                  <div className="row tn-confirm-actions">
                    <Button variant="tertiary" size="sm" onClick={() => setConfirming(null)}>Avbryt</Button>
                    <Button variant="danger" size="sm" disabled={saving} autoFocus onClick={() => confirmAction(d.key)}>
                      {d.ok}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="danger-soft"
                  size="sm"
                  className="tn-danger-btn"
                  disabled={saving}
                  onClick={() => setConfirming(d.key)}
                >
                  {d.title}
                </Button>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

export default SettingsTab
